import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Label } from "../../forms/Label/Label";
import { Select, type TSelectOption } from "@/components/forms";

type TAiComplianceFilterSelectVariant = "filter" | "form";

type TAiComplianceFilterSelectProps = {
  value?: string;
  onChange: (value: string) => void;
  variant?: TAiComplianceFilterSelectVariant;
  isLabelShown?: boolean;
  customLabel?: string;
  customPlaceholder?: string;
  disabled?: boolean;
  isClearable?: boolean;
  name?: string;
  id?: string;
};

const AiComplianceFilterSelect = ({
  value = "",
  onChange,
  variant = "filter",
  isLabelShown = true,
  customLabel,
  customPlaceholder,
  disabled = false,
  isClearable = true,
  name,
  id,
}: TAiComplianceFilterSelectProps) => {
  const { t } = useTranslation("common");
  const aiComplianceOptions = useMemo<TSelectOption[]>(() => {
    if (variant === "form") {
      return [
        { value: "All", label: t("ALL") },
        { value: "Yes", label: t("YES") },
        { value: "No", label: t("NO") },
      ];
    }

    return [
      { value: "True", label: t("YES") },
      { value: "False", label: t("NO") },
    ];
  }, [t, variant]);

  const fieldName =
    name ?? (variant === "form" ? "ai-compliance" : "ai-compliance-filter");
  const fieldId = id ?? fieldName;

  return (
    <div className="w-full">
      {isLabelShown && (
        <Label weight="medium" htmlFor={fieldId}>
          {customLabel ?? t("FILTER.AI_COMPLIANCE")}
        </Label>
      )}
      <Select
        name={fieldName}
        options={aiComplianceOptions}
        value={aiComplianceOptions.find((item) => item.value === value) ?? null}
        onChange={(option: TSelectOption | null) => {
          onChange(option?.value ?? "");
        }}
        getOptionLabel={(option) => option.label}
        getOptionValue={(option) => option.value}
        placeholder={
          customPlaceholder ??
          (variant === "form" ? "" : t("FILTER.SELECT_AI_COMPLIANCE"))
        }
        isClearable={isClearable}
        isDisabled={disabled}
      />
    </div>
  );
};

export default AiComplianceFilterSelect;
